import { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { useMutation } from '@apollo/client/react';

import { gql } from '@graphql/__generated__';
import { INSERT_IMAGE_ONE } from '@modules/_shared/components/image-gallery/imageGalleryQueries';
import type { ImageGalleryItem } from '@modules/_shared/components/image-gallery/NewImageGallery';
import { ImageGalleryDialog } from '@modules/_shared/components/image-upload/components/ImageGalleryDialog';
import { ImageUpload } from '@modules/_shared/components/image-upload/components/ImageUpload';
import { processImage } from '@modules/_shared/components/image-upload/processors/imageProcessor';
import type {
  ImageSource,
  ImageTransform,
} from '@modules/_shared/components/image-upload/types';
import RASnackbar from '@modules/_shared/components/RASnackbar';
import { useFileUpload } from '@modules/_shared/utils/files/file-upload';
import { useLocale } from '@shared/hooks/locale';

const INSERT_TEAM_IMAGE = gql(/* GraphQL */ `
  mutation InsertTeamImage($url: String!, $teamId: uuid!) {
    insert_team_images_one(
      object: { team_id: $teamId, image: { data: { url: $url } } }
    ) {
      id
      image {
        id
        url
      }
    }
  }
`);

const LINK_IMAGE_TO_TEAM = gql(/* GraphQL */ `
  mutation LinkImageToTeam($imageId: uuid!, $teamId: uuid!) {
    insert_team_images_one(object: { team_id: $teamId, image_id: $imageId }) {
      id
      image_id
      team_id
    }
  }
`);

const SET_TEAM_IMAGE = gql(/* GraphQL */ `
  mutation SetTeamImage($teamId: uuid!, $imageId: uuid!, $isLogo: Boolean!) {
    # Update logo
    updateLogo: update_teams_by_pk(
      pk_columns: { id: $teamId }
      _set: { logo_image_id: $imageId }
    ) @include(if: $isLogo) {
      id
      logo_image_id
      logo {
        id
        url
      }
    }

    # Update banner
    updateBanner: update_teams_by_pk(
      pk_columns: { id: $teamId }
      _set: { cover_photo_image_id: $imageId }
    ) @skip(if: $isLogo) {
      id
      cover_photo_image_id
      cover_photo {
        id
        url
      }
    }
  }
`);

type ImageType = 'logo' | 'banner';

type AgencyImageUploadModalProps = {
  teamId: string;
  teamName?: string | null;
  logoUrl?: string | null;
  imageType: ImageType;
  currentImageUrl?: string;
  open: boolean;
  onClose: () => void;
};

export const AgencyImageUploadModal = memo(
  ({
    teamId,
    teamName,
    logoUrl,
    imageType,
    currentImageUrl,
    open,
    onClose,
  }: AgencyImageUploadModalProps) => {
    const { t } = useLocale();
    const [isUploading, setIsUploading] = useState(false);
    const [showGallery, setShowGallery] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedSource, setSelectedSource] = useState<ImageSource | null>(
      null,
    );

    // Reset state when modal opens/closes
    useEffect(() => {
      if (open) {
        // Initialize state when opening
        if (currentImageUrl) {
          setSelectedSource({
            type: 'url',
            data: currentImageUrl,
            metadata: { isCurrentImage: true },
          });
          setShowGallery(false);
        } else {
          setSelectedSource(null);
          setShowGallery(true);
        }
      } else {
        // Clean up state when closing
        setSelectedSource(null);
        setShowGallery(false);
        setError(null);
      }
    }, [open, currentImageUrl]);

    const [uploadFile] = useFileUpload('image');
    const [insertTeamImage] = useMutation(INSERT_TEAM_IMAGE);
    const [linkImageToTeam] = useMutation(LINK_IMAGE_TO_TEAM);
    const [setTeamImage] = useMutation(SET_TEAM_IMAGE);
    const [insertImageOne] = useMutation(INSERT_IMAGE_ONE);

    const resetModalState = useCallback(() => {
      setSelectedSource(null);
      setError(null);
      setIsUploading(false);
      setShowGallery(false);
      onClose();
    }, [onClose]);

    const uploadImageToServer = useCallback(
      (file: File): Promise<{ id: string; url: string }> =>
        new Promise((resolve, reject) => {
          uploadFile(file, ({ data, error }) => {
            error || !data
              ? reject(new Error('Upload failed - no data returned'))
              : resolve({ id: data.id, url: data.url });
          });
        }),
      [uploadFile],
    );

    const saveTeamImage = useCallback(
      async (imageId: string) => {
        const isLogo = imageType === 'logo';
        const { data } = await setTeamImage({
          variables: { teamId, imageId, isLogo },
        });
        if ((isLogo ? data?.updateLogo : data?.updateBanner) == null) {
          throw new Error(
            t("Only this agency's leaders can change its photos"),
          );
        }
      },
      [setTeamImage, teamId, imageType, t],
    );

    const processAndSaveImage = useCallback(
      async (transform: ImageTransform) => {
        if (!selectedSource) {
          return;
        }

        setIsUploading(true);
        try {
          const isLogo = imageType === 'logo';
          const isFromGallery = selectedSource.metadata?.galleryId;
          const hasTransform = Object.keys(transform).length > 0;

          if (isFromGallery && !hasTransform) {
            // Gallery image without transformation: just update the team's logo_image_id or cover_photo_image_id
            await saveTeamImage(selectedSource.metadata?.galleryId as string);
          } else {
            // New upload or transformed image: process, upload to images
            const processedFile = await processImage(
              selectedSource,
              transform,
              t,
            );
            const { url } = await uploadImageToServer(processedFile);

            const { data } = await insertImageOne({
              variables: { url },
            });

            const imageId = data?.insert_images_one?.id;
            if (imageId) {
              // For banners, always create team_images entry
              if (!isLogo) {
                await linkImageToTeam({
                  variables: {
                    imageId,
                    teamId,
                  },
                });
              }

              await saveTeamImage(imageId);
            }
          }

          resetModalState();
        } catch (error) {
          setError(error instanceof Error ? error.message : 'Upload failed');
          setIsUploading(false);
        }
      },
      [
        selectedSource,
        t,
        uploadImageToServer,
        saveTeamImage,
        insertImageOne,
        linkImageToTeam,
        teamId,
        imageType,
        resetModalState,
      ],
    );

    const handleGalleryImageSelect = useCallback(
      (image: ImageGalleryItem) => {
        if (!image.image.url) {
          setError(t('Selected image has no URL'));
          return;
        }
        setShowGallery(false);
        setSelectedSource({
          type: 'url',
          data: image.image.url,
          metadata: { galleryId: image.image.id },
        });
      },
      [t],
    );

    const handleFilesUploaded = useCallback(
      async (uploadedFiles: { id: string; url: string }[]) => {
        try {
          for (const file of uploadedFiles) {
            await insertTeamImage({
              variables: {
                url: file.url,
                teamId,
              },
            });
          }
        } catch (error) {
          setError(
            error instanceof Error ? error.message : 'Failed to save images',
          );
        }
      },
      [insertTeamImage, teamId],
    );

    const imageUploadConfig = useMemo(() => {
      const isLogo = imageType === 'logo';
      return {
        title: isLogo ? t('Edit logo') : t('Edit banner image'),
        aspectRatio: isLogo ? 1 : 4.91,
        cropShape: 'rect' as const,
        enableCropping: !isLogo,
        maxWidth: isLogo ? ('sm' as const) : ('md' as const),
      };
    }, [imageType, t]);

    return (
      <>
        {selectedSource && !showGallery && (
          <ImageUpload
            source={selectedSource}
            currentImageUrl={currentImageUrl}
            open={open}
            onClose={resetModalState}
            onSave={processAndSaveImage}
            onChangePhoto={() => setShowGallery(true)}
            isLoading={isUploading}
            aspectRatio={imageUploadConfig.aspectRatio}
            cropShape={imageUploadConfig.cropShape}
            title={imageUploadConfig.title}
            enableCropping={imageUploadConfig.enableCropping}
            maxWidth={imageUploadConfig.maxWidth}
            previewLabel={teamName}
            previewLogoUrl={logoUrl}
            infoText={
              imageType === 'banner'
                ? t(
                    'Upload a cover image of at least 1472 x 300px and max 5Mb file size.',
                  )
                : undefined
            }
          />
        )}

        <ImageGalleryDialog
          open={open && showGallery}
          onClose={resetModalState}
          entityId={teamId}
          entityType="team"
          onSelectImage={handleGalleryImageSelect}
          onFilesUploaded={handleFilesUploaded}
        />

        {error && <RASnackbar message={error} onClose={() => setError(null)} />}
      </>
    );
  },
);
